import { People, PictureAsPdf, Print } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormHelperText,
  IconButton,
  TextField,
} from "@mui/material";
import { useEffect, useId, useMemo, useState } from "react";

import { DateChip } from "@/components/DateChip";
import { GroupedAutocomplete } from "@/components/GroupedAutocomplete";
import { ISBNMask } from "@/components/ISBNMask";
import { MultiSelectChip } from "@/components/MultiSelectChip";
import { SingleSelectChip } from "@/components/SingleSelectChip";
import { useAddParticipant, useCreateProofingTitle } from "@/domain/proofing-flow/mutationHooks";
import {
  useFetchPackages,
  useFetchProjectsWithPackages,
  useFetchSuppliersByProject,
  useFetchUsersByProject,
} from "@/domain/usersAndProjects/queryHooks";
import {
  enrichPackagesWithProjects,
  type PackageOption,
} from "@/domain/usersAndProjects/utils/packageEnrichment";
import { transformSuppliers } from "@/domain/usersAndProjects/utils/supplierTransform";
import { PROOFING_FLOW_DOCUMENTATION_URL } from "@/lib/constants";
import { useAuth } from "@/providers/authProvider";

import {
  ActionsContainer,
  CreateButton,
  CreateProofingTitleBtn,
  FooterContainer,
  FormFieldsContainer,
  IsbnField,
  ModalHeader,
  NetSuiteField,
  PackageSelectField,
  PillsContainer,
  ProjectNameField,
  TwoColumnFieldsContainer,
} from "./createProofingTitle.styles";

export type { PackageOption };

export interface Participant {
  id: string;
  name: string | null;
  email: string | null;
}

export interface ProofingProjectFormData {
  packageId: string;
  proofingProjectName: string;
  netsuiteProjectNumber?: string;
  isbn?: string;
  dueDate?: Date;
  printDate?: Date;
  setter?: string;
}

export interface CreateProofingTitleProps {
  disabled?: boolean;
}

export const CreateProofingTitle = ({ disabled = false }: CreateProofingTitleProps) => {
  const [selectedPackage, setSelectedPackage] = useState<PackageOption | null>(null);
  const [proofingProjectName, setProofingProjectName] = useState<string>("");
  const [netsuiteProjectNumber, setNetsuiteProjectNumber] = useState<string>("");
  const [isbn, setIsbn] = useState<string>("");
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [printDate, setPrintDate] = useState<Date | null>(null);
  const [selectedParticipants, setSelectedParticipants] = useState<Participant[]>([]);
  const [selectedSetter, setSelectedSetter] = useState<string | null>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  // Hook for adding participants to the created title
  const { mutateAsync: addParticipant, isPending: isAddingParticipants } = useAddParticipant({
    onError: (error) => {
      console.error("Failed to add participant:", error);
      // Don't block the flow, but log the error
      // The title is already created, so we can still consider it a success
    },
  });

  const { mutateAsync: createProofingTitleAsync, isPending: isCreating } = useCreateProofingTitle({
    onError: (error) => {
      setSubmissionError(error.message || "Failed to create proofing title");
    },
  });

  // Generate unique IDs for accessibility
  const dialogTitleId = useId();
  const packageHelperTextId = useId();

  // Get current user from auth context
  const { user } = useAuth();

  // Fetch data only when modal is open to prevent unnecessary initial load
  const {
    packages: rawPackages,
    isLoading: packagesLoading,
    error: packagesError,
  } = useFetchPackages({ enabled: open });
  const {
    projectsWithPackages,
    isLoading: projectsLoading,
    error: projectsError,
  } = useFetchProjectsWithPackages(user?.sub, user?.tenantSlug, { enabled: open });

  const {
    suppliers,
    isLoading: suppliersLoading,
    error: suppliersError,
  } = useFetchSuppliersByProject(selectedPackage?.projectId || null);

  // Enrich packages with project information
  const enrichedPackages = useMemo(() => {
    return enrichPackagesWithProjects(rawPackages, projectsWithPackages);
  }, [rawPackages, projectsWithPackages]);
  const noPackagesText =
    enrichedPackages.length <= 0
      ? "No packages available because you don't have access to any project. Please contact your administrator"
      : "No packages matching your search";
  const {
    users,
    isLoading: usersLoading,
    error: usersError,
  } = useFetchUsersByProject(selectedPackage?.projectId ?? null);

  // Transform suppliers to name array and ID map
  const { names: supplierNames, nameToIdMap } = useMemo(
    () => transformSuppliers(suppliers),
    [suppliers],
  );

  // Compute loading and error states
  const isDataLoading = packagesLoading || projectsLoading || usersLoading;
  const hasError = packagesError || projectsError || usersError || suppliersError;
  const errorMessage =
    packagesError?.message ||
    projectsError?.message ||
    usersError?.message ||
    suppliersError?.message;

  // Auto-fill project name when package changes
  useEffect(() => {
    if (selectedPackage) {
      setProofingProjectName(selectedPackage.label);
    } else {
      setProofingProjectName("");
    }
  }, [selectedPackage]);

  // Auto-select current user as participant when dialog opens
  useEffect(() => {
    if (open && user && users && users.length > 0 && selectedParticipants.length === 0) {
      const currentUser = users.find((u) => u.id === user.sub);
      if (currentUser) {
        setSelectedParticipants([currentUser]);
      }
    }
  }, [open, user, users, selectedParticipants.length]);

  const handleIsbnChange = (event: { target: { value: string } }) => {
    setIsbn(event.target.value);
  };

  const onClose = () => {
    setOpen(false);
  };

  const handleCreateProject = () => {
    if (selectedPackage) {
      setSubmissionError(null);

      // Format dates as YYYY-MM-DD
      const formatDateToYYYYMMDD = (date: Date | null): string | null => {
        if (!date) return null;
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      // Use mutateAsync to properly await the completion
      createProofingTitleAsync({
        packageId: selectedPackage.id,
        name: proofingProjectName,
        pNumber: netsuiteProjectNumber || null,
        isbn: isbn ? isbn.replace(/\D/g, "") : null,
        dueDate: formatDateToYYYYMMDD(dueDate),
        printDate: formatDateToYYYYMMDD(printDate),
        state: "draft", // New titles always start as draft
        opco: user?.tenantSlug || "", // Use tenant slug as operating company identifier
        supplierId: selectedSetter ? (nameToIdMap?.get(selectedSetter) ?? null) : null,
      })
        .then(async (data) => {
          if (!data) {
            throw new Error("Failed to create proofing title");
          }
          // After successfully creating the title, add participants
          // Ensure current user is always included as a participant
          const participantsToAdd = [...selectedParticipants];
          if (user?.sub && !participantsToAdd.some((p) => p.id === user.sub)) {
            // Current user not in list, add them
            const currentUser = users?.find((u) => u.id === user.sub);
            if (currentUser) {
              participantsToAdd.push(currentUser);
            }
          }

          if (participantsToAdd.length > 0) {
            // Add all participants in parallel and wait for completion
            await Promise.all(
              participantsToAdd.map((participant) =>
                addParticipant({
                  titleId: data.id,
                  userId: participant.id,
                }),
              ),
            );
          }
          handleClose();
        })
        .catch((error) => {
          // Error already handled by onError callback
          console.error("Error in handleCreateProject:", error);
        });
    }
  };

  const handleClose = () => {
    setSelectedPackage(null);
    setProofingProjectName("");
    setNetsuiteProjectNumber("");
    setIsbn("");
    setDueDate(null);
    setPrintDate(null);
    setSelectedParticipants([]);
    setSelectedSetter(null);
    setSubmissionError(null);
    onClose();
  };

  const isFormValid = selectedPackage !== null;
  const showAdditionalFields = selectedPackage !== null;
  const isSubmitting = isCreating || isAddingParticipants;

  return (
    <>
      <CreateProofingTitleBtn
        onClick={() => setOpen(true)}
        variant="contained"
        color="primary"
        disableElevation
        startIcon={<AddIcon />}
        disabled={disabled}
      >
        New proofing title
      </CreateProofingTitleBtn>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        aria-labelledby={dialogTitleId}
        PaperProps={{
          sx: { maxWidth: "580px" },
        }}
      >
        <ModalHeader>
          <DialogTitle id={dialogTitleId}>Create new proofing title</DialogTitle>
          <IconButton aria-label="close" size="small" onClick={handleClose} disabled={isSubmitting}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </ModalHeader>

        <DialogContent>
          <FormFieldsContainer>
            {hasError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errorMessage || "Failed to load data. Please try again."}
              </Alert>
            )}

            {isDataLoading && (
              <Alert severity="info" icon={<CircularProgress size={20} />} sx={{ mb: 2 }}>
                Loading packages and projects...
              </Alert>
            )}

            <PackageSelectField fullWidth>
              <GroupedAutocomplete
                options={enrichedPackages}
                value={selectedPackage}
                onChange={(value) => {
                  if (!value || Array.isArray(value)) {
                    setSelectedPackage(null);
                    return;
                  }
                  // GroupedAutocomplete.onChange emits only { id, label } — it strips all
                  // extra fields. Look up the full enriched option (which carries
                  // projectId/projectName) by id. If not found (e.g. options changed between
                  // render and selection), fall back to null so that projectId is never
                  // silently undefined and supplier/user queries are never incorrectly enabled.
                  const full = enrichedPackages.find((pkg) => pkg.id === value.id);
                  setSelectedPackage(full ?? null);
                }}
                label="Select a package *"
                placeholder="Search for a package..."
                disabled={isDataLoading || !!hasError}
                noOptionsText={noPackagesText}
                groupConfig={{
                  getGroupKey: (option) => (option as PackageOption).projectId || "no-project",
                  getGroupLabel: (groupKey) => {
                    if (groupKey === "no-project") {
                      return "No Project";
                    }
                    const opt = enrichedPackages.find((pkg) => pkg.projectId === groupKey);
                    return opt?.projectName || groupKey;
                  },
                  getSecondaryLabel: (option) => (option as PackageOption).projectName || "",
                }}
              />

              <FormHelperText id={packageHelperTextId}>
                The package that this title belongs to.{" "}
                {/** biome-ignore lint/a11y/noAmbiguousAnchorText: url can be ambiguous */}
                <a href={PROOFING_FLOW_DOCUMENTATION_URL} target="_blank" rel="noopener noreferrer">
                  Learn more
                </a>
              </FormHelperText>
            </PackageSelectField>

            {showAdditionalFields && (
              <>
                <ProjectNameField fullWidth>
                  <TextField
                    fullWidth
                    label="Proofing title name"
                    value={proofingProjectName}
                    onChange={(e) => setProofingProjectName(e.target.value)}
                    required
                    helperText="Suggested from selected package. You can change it."
                  />
                </ProjectNameField>

                {submissionError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {submissionError}
                  </Alert>
                )}

                <TwoColumnFieldsContainer>
                  <NetSuiteField>
                    <TextField
                      fullWidth
                      label="NetSuite Project number"
                      value={netsuiteProjectNumber}
                      onChange={(e) => setNetsuiteProjectNumber(e.target.value)}
                      placeholder="e.g. P00013799"
                    />
                  </NetSuiteField>

                  <IsbnField>
                    <TextField
                      fullWidth
                      label="ISBN"
                      value={isbn}
                      onChange={handleIsbnChange}
                      placeholder="978 - 12 - 34 - 56789 - 0"
                      InputProps={{
                        inputComponent: ISBNMask as never,
                      }}
                    />
                  </IsbnField>
                </TwoColumnFieldsContainer>

                <Divider sx={{ my: 0 }} />

                <PillsContainer>
                  <DateChip key="due-date" label="Due date" value={dueDate} onChange={setDueDate} />
                  <DateChip
                    key="print-date"
                    label="Print date"
                    icon={<Print sx={{ fontSize: 18 }} />}
                    value={printDate}
                    onChange={setPrintDate}
                  />
                  <SingleSelectChip
                    label="Setter"
                    options={supplierNames}
                    value={selectedSetter}
                    onChange={setSelectedSetter}
                    getOptionLabel={(option: string) => option}
                    getOptionKey={(option: string) => option}
                    placeholder="Search setter companies..."
                    icon={<PictureAsPdf sx={{ fontSize: 18 }} />}
                    disabled={suppliersLoading || !selectedPackage?.projectId}
                  />
                  <MultiSelectChip
                    label="Participants"
                    options={users || []}
                    value={selectedParticipants}
                    onChange={setSelectedParticipants}
                    getOptionLabel={(participant) => participant.name || "unknown user"}
                    getOptionKey={(participant) => participant.id}
                    placeholder="Search participants..."
                    icon={<People sx={{ fontSize: 18 }} />}
                    disabled={usersLoading}
                  />
                </PillsContainer>
              </>
            )}
          </FormFieldsContainer>
        </DialogContent>

        <FooterContainer>
          <ActionsContainer>
            <Button type="button" onClick={handleClose} color="inherit" disabled={isSubmitting}>
              Cancel
            </Button>
            <CreateButton
              variant="contained"
              disabled={!isFormValid || isSubmitting}
              onClick={handleCreateProject}
              startIcon={isSubmitting ? <CircularProgress size={20} /> : undefined}
            >
              {isSubmitting ? "Creating..." : "Create proofing title"}
            </CreateButton>
          </ActionsContainer>
          {/* <FooterHelperText variant="caption">
            Participants will only be notified once a proofing round is started.
          </FooterHelperText> */}
        </FooterContainer>
      </Dialog>
    </>
  );
};
